/**
 * CONTENT RELEASES — scheduled content updates, switched on by the SERVER'S clock (docs/RELEASES.md).
 *
 * A piece of content (a plant, decor item, fish, insect — anything with a def) can name the release it belongs to with
 * `release: "<id>"`. Until that release is on, the game neither shows nor grants it; at the release moment it turns on
 * by itself, in every running game, with no deploy. That is what lets a content package's code reach live servers a week or
 * two EARLY, switched off, and go out at an exact minute with nobody at the PC — and what makes a rollback a switch,
 * not a redeploy.
 *
 *   • `RELEASES` below is the registry — one entry per release, as data, like the guide's chapters.
 *   • The STATE of each release (off / preview / on, and the moment `at` it turns on) is the server's, set in the admin
 *     console and sent with /api/config; `releaseView` folds it with the server-synced clock into what this client shows.
 *   • UNTAGGED CONTENT IS ALWAYS ON. Everything in the game today is untagged, so this changes nothing until a release
 *     is declared and content is tagged with it.
 *
 * FAIL SAFE BOTH WAYS: on LIVE a release the server has no state for is OFF (a missing setting must never leak an
 * content package); on the TEST server every release is ON unless the admin asks to `simulate` the live view, because the
 * test server exists to play the unreleased content.
 *
 * The server mirrors this decision for everything it grants or accepts in a save (Phase 2 of the release plan) — a
 * client-side filter alone would let a modified client hand itself unreleased content.
 */

export interface ReleaseDef {
  /** the name players see ("Spring Bloom") */
  title: string;
  /** the planned release moment, ISO 8601 UTC — the admin panel's default; the server's `at` is what counts */
  at?: string;
  /** when teasers ("Coming in Spring Bloom") may start — the admin panel's default for the preview window */
  previewFrom?: string;
}

/** Every content release. Add one here before tagging content with it. */
export const RELEASES = {
} satisfies Record<string, ReleaseDef>;

export type ReleaseId = keyof typeof RELEASES;

/**
 * A release's state on the server. `mode` is what it shows BEFORE `at`: "off" (nothing), "preview" (teased, locked) or
 * "on" (released). Once the server's clock passes `at` it is on whatever `mode` says — that is the schedule. `simulate`
 * (test server only) shows the live view instead of everything-on, to test the switched-off path.
 */
export interface ReleaseState {
  mode: "off" | "preview" | "on";
  at?: number | null;
  simulate?: boolean;
}
export type ReleaseStates = Readonly<Record<string, ReleaseState | undefined>>;
export type ReleaseView = "off" | "preview" | "on";

/** What a release looks like right now. `id` undefined = untagged content, always on. */
export function releaseView(
  id: string | undefined, states: ReleaseStates, now: number, env: "production" | "staging" = "production",
): ReleaseView {
  if (!id) return "on";
  const st = states[id];
  if (env === "staging" && !st?.simulate) return "on";
  if (!st) return "off";
  if (st.at != null && Number.isFinite(st.at) && now >= st.at) return "on";
  return st.mode;
}

/** Is this content available to play with (shown, sold, granted)? */
export function released(id: string | undefined, states: ReleaseStates, now: number, env: "production" | "staging" = "production"): boolean {
  return releaseView(id, states, now, env) === "on";
}

// ── THE LIVE STATE — what the server last said, and the one question every gate asks ──────────────────────────
//
// Set from /api/config (sim/env.ts) at boot and whenever it changes (watchLaunchModes). Until the server has answered,
// the state is EMPTY on "production", so anything tagged is OFF — a slow or failed config read can never leak an
// content package. The test server answers "staging", which shows everything.

let liveStates: Record<string, ReleaseState> = {};
let liveEnv: "production" | "staging" = "production";
let liveVersion = 0;
const listeners = new Set<() => void>();
let clock: () => number = () => Date.now();

/** Record what /api/config said. Listeners run only when something actually changed. */
export function setReleaseConfig(states: Record<string, ReleaseState>, env: "production" | "staging"): void {
  const same = env === liveEnv && JSON.stringify(states) === JSON.stringify(liveStates);
  if (same) return;
  liveStates = states;
  liveEnv = env;
  bump();
  scheduleNext();
}
function bump(): void {
  liveVersion++;
  for (const fn of listeners) fn();
}
/** A SCHEDULED release turns on by the clock, with nothing new from the server — so wake the listeners at that
 *  moment (re-armed for the one after it), and every open game shows the update at the minute, without a reload. */
let wake: ReturnType<typeof setTimeout> | null = null;
function scheduleNext(): void {
  if (wake) { clearTimeout(wake); wake = null; }
  const now = clock();
  const next = Object.values(liveStates).map((s) => s?.at ?? null)
    .filter((t): t is number => t != null && Number.isFinite(t) && t > now).sort((a, b) => a - b)[0];
  if (next == null || typeof setTimeout !== "function") return;
  // (capped: a timer more than ~24 days out overflows; the config poll re-arms it long before then)
  wake = setTimeout(() => { wake = null; bump(); scheduleNext(); }, Math.min(next - now + 250, 2 ** 31 - 1));
}
/** Be told when release states change (caches built from catalogues — the Collection, the daily bonus — rebuild). */
export function onReleaseChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}
/** Bumps on every change — a cheap key for memoising anything filtered by release. */
export function releaseVersion(): number { return liveVersion; }
/** The clock releases are judged by: the server-synced game clock (wired at boot, sim/env.ts). */
export function setReleaseClock(now: () => number): void { clock = now; }
/** The current states and server, for the admin panel and the guard tests. */
export function releaseConfig(): { states: Readonly<Record<string, ReleaseState>>; env: "production" | "staging" } {
  return { states: liveStates, env: liveEnv };
}

/**
 * THE GATE: is this piece of content out of reach for players right now? Every catalogue, spawn table, shop, grant
 * and list asks this of a def (anything with an optional `release`). Untagged → never out. A release that is merely
 * in PREVIEW is still out — teasers are drawn separately, never sold or caught.
 */
export function isOut(def: { release?: string } | null | undefined): boolean {
  return !!def?.release && !released(def.release, liveStates, clock(), liveEnv);
}
/** `isOut`, negated — for filters. */
export function isIn(def: { release?: string } | null | undefined): boolean { return !isOut(def); }

/**
 * TEASERS — is this content "👀 Coming soon" (its release in PREVIEW)? Then the shop, the Market and the Field Guide
 * show it LOCKED, named, with the release it arrives in — never sellable, plantable or catchable (it is still `isOut`;
 * the server refuses it the same way). Hidden content (off) is never teased.
 */
export function isPreview(def: { release?: string } | null | undefined): boolean {
  return !!def?.release && releaseView(def.release, liveStates, clock(), liveEnv) === "preview";
}
/** What a teaser says: the release's name and, when the server has a time set, the moment it arrives. */
export function teaserOf(def: { release?: string } | null | undefined): { title: string; at: number | null } {
  const id = def?.release ?? "";
  const title = (RELEASES as Record<string, ReleaseDef>)[id]?.title ?? "a coming update";
  const at = liveStates[id]?.at ?? null;
  return { title, at: at != null && Number.isFinite(at) ? at : null };
}
/** A teaser's line: "Coming in Creature Raising · 1 Apr", or "Coming in Creature Raising". */
export function teaserText(def: { release?: string } | null | undefined): string {
  const t = teaserOf(def);
  const day = t.at != null ? new Date(t.at).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "";
  return `Coming in ${t.title}${day ? ` · ${day}` : ""}`;
}

/** Release states as sent by /api/config, cleaned. Anything malformed is dropped (so it reads as unconfigured: OFF on
 *  live). */
export function coerceReleaseStates(raw: unknown): Record<string, ReleaseState> {
  const out: Record<string, ReleaseState> = {};
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return out;
  for (const [id, v] of Object.entries(raw as Record<string, unknown>)) {
    if (!v || typeof v !== "object") continue;
    const o = v as Record<string, unknown>;
    if (o.mode !== "off" && o.mode !== "preview" && o.mode !== "on") continue;
    const at = typeof o.at === "number" && Number.isFinite(o.at) ? o.at : null;
    out[id] = { mode: o.mode, at, ...(o.simulate === true ? { simulate: true } : {}) };
  }
  return out;
}
